/**
 * Copyright © 2026 Adnuntius AS.
 */
import {DOCUMENT, AfterViewInit, Component, ElementRef, Inject, OnDestroy, OnInit, forwardRef, signal} from '@angular/core';
import {BsModalRef} from 'ngx-bootstrap/modal';
import {Subject} from 'rxjs';

import template from './targeting-editor-modal.component.html';
import {TargetingHelper} from './targeting-helper';
import {
  TargetingEditorOptions,
  TargetingEditorResult,
  TargetingEditorState
} from './targeting-editor-state';
import {LocalNetworkProfile} from '../../components/session/local-network-profile';
import {LocalUserPermissions} from '../../components/session/local-user-permissions';
import {LocalUserProfile} from '../../components/session/local-user-profile';
import {LocalNetworkInfo} from '../../components/session/local-network-info';

import {TARGETING_IMPORTS} from './targeting-imports';
import {ReachAnalysisComponent} from '../common/controller/reach-analysis.component';
import {CopyTargetingTemplatesComponent} from './copy-targeting-templates.component';
import {CopyFromLineItemComponent} from './copy-from-line-item.component';
import {MatchingGroupsComponent} from './matching-groups.component';
import {UrlTargetingComponent} from './targeting/url-targeting.component';
import {AdUnitTargetingComponent} from './targeting/adunit-targeting.component';
import {KeyValueTargetingComponent} from './targeting/key-value-targeting.component';
import {SemanticTargetingComponent} from './targeting/semantic-targeting.component';
import {SimilarKeywordTargetingComponent} from './targeting/similar-keyword-targeting.component';
import {DateTargetingComponent} from './targeting/date-targeting.component';
import {PublisherTargetingComponent} from './targeting/publisher-targeting.component';
import {SiteGroupTargetingComponent} from './targeting/site-group-targeting.component';
import {GeospatialTargetingComponent} from './targeting/geospatial-targeting.component';
import {CategoryTargetingComponent} from './targeting/category-targeting.component';
import {ViewabilityTargetingComponent} from './targeting/viewability-targeting.component';
import {DomainTargetingComponent} from './targeting/domain-targeting.component';
import {IpAddressTargetingComponent} from './targeting/ip-address-targeting.component';
import {SegmentTargetingComponent} from './targeting/segment-targeting.component';
import {AdUnitMatchingLabelTargetingComponent} from './targeting/ad-unit-matching-label-targeting.component';
import {RetargetingTargetingComponent} from './targeting/retargeting-targeting.component';
import {NamedLocationTargetingComponent} from './targeting/named-location-targeting.component';
import {SiteTargetingComponent} from './targeting/site-targeting.component';
import {ThirdPartyAudienceTargetingComponent} from './targeting/third-party-audience-targeting.component';
import {SiteCountryTargetingComponent} from './targeting/site-country-targeting.component';
import {DayPartingTargetingComponent} from './targeting/day-parting-targeting.component';
import {WeatherTargetingComponent} from './targeting/weather-targeting.component';
import {DeviceTargetingComponent} from './targeting/device-targeting.component';
import {KeywordTargetingComponent} from './targeting/keyword-targeting.component';

@Component({
  imports: [
    ...TARGETING_IMPORTS,
    forwardRef(() => ReachAnalysisComponent),
    CopyTargetingTemplatesComponent,
    CopyFromLineItemComponent,
    MatchingGroupsComponent,
    UrlTargetingComponent,
    AdUnitTargetingComponent,
    KeyValueTargetingComponent,
    SemanticTargetingComponent,
    SimilarKeywordTargetingComponent,
    DateTargetingComponent,
    PublisherTargetingComponent,
    SiteGroupTargetingComponent,
    GeospatialTargetingComponent,
    CategoryTargetingComponent,
    ViewabilityTargetingComponent,
    DomainTargetingComponent,
    IpAddressTargetingComponent,
    SegmentTargetingComponent,
    AdUnitMatchingLabelTargetingComponent,
    RetargetingTargetingComponent,
    NamedLocationTargetingComponent,
    SiteTargetingComponent,
    ThirdPartyAudienceTargetingComponent,
    SiteCountryTargetingComponent,
    DayPartingTargetingComponent,
    WeatherTargetingComponent,
    DeviceTargetingComponent,
    KeywordTargetingComponent
  ],
  selector: 'adn-targeting-editor-modal',
  template,
  styles: [`
    :host {
      display: flex;
      flex-direction: column;
      height: 100%;
      min-height: 0;
    }

    form {
      display: flex;
      flex-direction: column;
      height: 100%;
      min-height: 0;
      margin: 0;
    }

    .modal-header,
    .modal-footer {
      flex: 0 0 auto;
    }

    .modal-body {
      flex: 1 1 auto;
      overflow-y: auto;
      min-height: 0;
    }
  `]
})
export class TargetingEditorModalComponent implements OnInit, AfterViewInit, OnDestroy, TargetingEditorOptions {
  /** Set via BsModalService initialState */
  targeting: any;
  modelLineItem: any;
  pageType: string;
  reachEnabled: string;
  availableTargets: any;
  mandatoryTargets: any;
  overridableTargeting: any;
  targetingMode: string;
  lineItemType: string;
  isLockedDown: boolean;

  readonly onSaved = new Subject<TargetingEditorResult>();
  readonly saving = signal(false);

  private readonly stateState = signal<TargetingEditorState | null>(null, {equal: () => false});
  get state(): TargetingEditorState { return this.stateState() as TargetingEditorState; }
  set state(value: TargetingEditorState) { this.stateState.set(value); }

  private initialTargetTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    public readonly bsModalRef: BsModalRef,
    private readonly elementRef: ElementRef,
    @Inject(DOCUMENT) private readonly document: Document,
    @Inject(LocalNetworkProfile) private readonly LocalNetworkProfile: any,
    @Inject(LocalUserPermissions) private readonly LocalUserPermissions: any,
    @Inject(LocalUserProfile) private readonly localUserProfile: any,
    private readonly localNetworkInfo: LocalNetworkInfo = new LocalNetworkInfo()
  ) {
  }

  ngOnInit(): void {
    this.document.addEventListener('keydown', this.handleModalEscape, true);
    this.state = new TargetingEditorState({
      targeting: this.targeting,
      modelLineItem: this.modelLineItem,
      pageType: this.pageType,
      reachEnabled: this.reachEnabled,
      availableTargets: this.availableTargets,
      mandatoryTargets: this.mandatoryTargets,
      overridableTargeting: this.overridableTargeting,
      targetingMode: this.targetingMode,
      lineItemType: this.lineItemType,
      isLockedDown: this.isLockedDown
    }, {
      LocalNetworkProfile: this.LocalNetworkProfile,
      LocalUserPermissions: this.LocalUserPermissions,
      localUserProfile: this.localUserProfile,
      localNetworkInfo: this.localNetworkInfo
    });
    this.state.tabController.onAction = () => {
      this.state.calcSizes();
      this.state = this.state;
    };
  }

  ngAfterViewInit(): void {
    const initialTarget = this.state?.targetEntries?.[0]?.target;
    if (!initialTarget?.widget) {
      return;
    }
    this.initialTargetTimer = setTimeout(() => {
      this.initialTargetTimer = null;
      this.state.tabController.onTabSelect(initialTarget.widget, false);
    });
  }

  ngOnDestroy(): void {
    this.document.removeEventListener('keydown', this.handleModalEscape, true);
    if (this.initialTargetTimer) {
      clearTimeout(this.initialTargetTimer);
      this.initialTargetTimer = null;
    }
  }

  onTargetSelected(entry: {key: string; target: any}): void {
    if (this.initialTargetTimer) {
      clearTimeout(this.initialTargetTimer);
      this.initialTargetTimer = null;
    }
    this.state.onTargetTabSelect(entry.target);
  }

  handleModalKeypress(event: KeyboardEvent): void {
    if (TargetingHelper.shouldSuppressModalEnter(event)) {
      TargetingHelper.stopEvent(event);
    }
  }

  saveAndClose(): void {
    if (this.saving()) {
      return;
    }
    this.saving.set(true);
    this.state.tabController.tabBeforeSubmit().then(() => {
      this.onSaved.next({
        wc: this.state.underModel || this.state.targeting,
        overrides: this.state.overridableTargeting,
        reset: this.state.resetObj.reset
      });
      this.bsModalRef.hide();
    }, () => {
      this.saving.set(false);
    });
  }

  dismiss(): void {
    this.bsModalRef.hide();
  }

  readonly handleModalEscape = (event: KeyboardEvent): void => {
    if ((event.key !== 'Escape' && event.keyCode !== 27) || !this.isTopModal()) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    this.dismiss();
  };

  private isTopModal(): boolean {
    const modalContainers = this.document.querySelectorAll('modal-container');
    const topModal = modalContainers.item(modalContainers.length - 1);
    return !!topModal && topModal.contains(this.elementRef.nativeElement);
  }
}
